import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import { cn } from "../../../../shared/ui/cn";
import {
    GROUP_SIZES,
    PAY_RATE_TYPES,
    normalizeRate,
    validateRate,
    type PayRateType,
    type PayRates,
    type RateError,
} from "../../domain/PayRates";

interface Props {
    /** Prefix for the fields' ids, so two forms on a page do not collide. */
    idPrefix: string;
    value: PayRates;
    onSave: (rates: PayRates) => Promise<unknown>;
    saving?: boolean;
    /** What an empty field falls back to, shown as its placeholder. */
    fallback?: PayRates;
    /** Extra action beside save — a cancel in a dialog. */
    secondaryAction?: React.ReactNode;
}

interface Draft {
    individual: string;
    couple: string;
    group: string;
    sizes: Record<string, string>;
}

const toDraft = (rates: PayRates): Draft => ({
    individual: rates.individual ?? "",
    couple: rates.couple ?? "",
    group: rates.group ?? "",
    sizes: Object.fromEntries(
        Object.entries(rates.group_sizes ?? {}).map(([size, amount]) => [size, amount ?? ""])
    ),
});

/**
 * Optional amounts: an individual, a couple and a group session — and, for a
 * group, one per number of members from 2 to 20 where it should differ.
 *
 * Empty means "not set", never zero — a report shows the two differently —
 * so every field may be left blank, and saving everything blank removes them.
 * The nineteen group sizes are folded away until wanted: most centres pay
 * every group alike, and those that do not usually vary a handful of sizes.
 */
export default function PayRatesForm({
    idPrefix,
    value,
    onSave,
    saving = false,
    fallback,
    secondaryAction,
}: Props) {
    const { t, i18n } = useTranslation();
    const [draft, setDraft] = useState<Draft>(() => toDraft(value));
    const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
    const configuredSizes = Object.values(draft.sizes).filter((amount) => amount.trim() !== "").length;
    const [sizesOpen, setSizesOpen] = useState(configuredSizes > 0);

    // Filled again whenever the saved value changes underneath the form.
    useEffect(() => setDraft(toDraft(value)), [value]);

    const errors = Object.fromEntries(
        PAY_RATE_TYPES.map((type) => [type, validateRate(draft[type])])
    ) as Record<PayRateType, RateError | null>;
    const sizeErrors = Object.fromEntries(
        GROUP_SIZES.map((size) => [size, validateRate(draft.sizes[size] ?? "")])
    ) as Record<number, RateError | null>;
    const invalid =
        PAY_RATE_TYPES.some((type) => errors[type] !== null) ||
        GROUP_SIZES.some((size) => sizeErrors[size] !== null);

    const money = (amount: string | null | undefined) =>
        amount
            ? new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(Number(amount))
            : null;
    const placeholder = (type: PayRateType) => money(fallback?.[type]) ?? t("reports.rates.placeholder");
    // An empty size takes, in order: the centre's rate for that size, then the
    // general group rate as typed here, then the centre's general one.
    const sizePlaceholder = (size: number) =>
        money(fallback?.group_sizes?.[String(size)]) ??
        money(normalizeRate(draft.group)) ??
        money(fallback?.group) ??
        t("reports.rates.sizes.useGeneral");

    const touch = () => setStatus("idle");

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (invalid) return;
        setStatus("idle");
        const groupSizes = Object.fromEntries(
            Object.entries(draft.sizes)
                .map(([size, amount]) => [size, normalizeRate(amount)] as const)
                .filter(([, amount]) => amount !== null)
        );
        try {
            await onSave({
                individual: normalizeRate(draft.individual),
                couple: normalizeRate(draft.couple),
                group: normalizeRate(draft.group),
                group_sizes: groupSizes,
            });
            setStatus("saved");
        } catch {
            setStatus("error");
        }
    };

    const sizesPanel = `${idPrefix}-group-sizes`;

    return (
        <form onSubmit={submit} noValidate className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {PAY_RATE_TYPES.map((type) => {
                    const id = `${idPrefix}-${type}`;
                    const error = errors[type];
                    return (
                        <div key={type} className="space-y-1.5">
                            <Label htmlFor={id}>{t(`reports.rates.${type}`)}</Label>
                            <Input
                                id={id}
                                inputMode="decimal"
                                value={draft[type]}
                                placeholder={placeholder(type)}
                                onChange={(event) => {
                                    touch();
                                    setDraft((current) => ({ ...current, [type]: event.target.value }));
                                }}
                                endIcon={<span aria-hidden="true">€</span>}
                                error={error !== null}
                                aria-invalid={error !== null}
                                aria-describedby={error ? `${id}-error` : undefined}
                            />
                            {error && (
                                <p id={`${id}-error`} className="text-xs text-status-critical">
                                    {t(`reports.rates.errors.${error}`)}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-800">
                <button
                    type="button"
                    aria-expanded={sizesOpen}
                    aria-controls={sizesPanel}
                    onClick={() => setSizesOpen((open) => !open)}
                    className="flex min-h-[44px] w-full items-center justify-between gap-3 px-4 py-2 text-left"
                >
                    <span>
                        <span className="block text-sm font-medium text-content-primary">
                            {t("reports.rates.sizes.title")}
                        </span>
                        <span className="block text-xs text-content-secondary">
                            {configuredSizes > 0
                                ? t("reports.rates.sizes.configured", { count: configuredSizes })
                                : t("reports.rates.sizes.none")}
                        </span>
                    </span>
                    <ChevronDown
                        className={cn("h-4 w-4 shrink-0 text-content-tertiary transition-transform", sizesOpen && "rotate-180")}
                        aria-hidden="true"
                    />
                </button>
                {sizesOpen && (
                    <div id={sizesPanel} className="border-t border-gray-200 px-4 pb-4 pt-3 dark:border-gray-800">
                        <p className="mb-3 text-xs text-content-secondary">{t("reports.rates.sizes.hint")}</p>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                            {GROUP_SIZES.map((size) => {
                                const id = `${idPrefix}-group-${size}`;
                                const error = sizeErrors[size];
                                return (
                                    <div key={size} className="space-y-1">
                                        <Label htmlFor={id} className="text-xs">
                                            {t("reports.rates.sizes.label", { count: size })}
                                        </Label>
                                        <Input
                                            id={id}
                                            inputMode="decimal"
                                            value={draft.sizes[size] ?? ""}
                                            placeholder={sizePlaceholder(size)}
                                            onChange={(event) => {
                                                touch();
                                                setDraft((current) => ({
                                                    ...current,
                                                    sizes: { ...current.sizes, [size]: event.target.value },
                                                }));
                                            }}
                                            endIcon={<span aria-hidden="true">€</span>}
                                            error={error !== null}
                                            aria-invalid={error !== null}
                                            aria-describedby={error ? `${id}-error` : undefined}
                                        />
                                        {error && (
                                            <p id={`${id}-error`} className="text-xs text-status-critical">
                                                {t(`reports.rates.errors.${error}`)}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <p role="status" aria-live="polite" className="text-sm sm:mr-auto">
                    {status === "saved" && <span className="text-status-success">{t("reports.rates.saved")}</span>}
                    {status === "error" && <span className="text-status-critical">{t("reports.rates.saveError")}</span>}
                    {invalid && sizesOpen === false && GROUP_SIZES.some((size) => sizeErrors[size]) && (
                        <span className="text-status-critical">{t("reports.rates.sizes.fixErrors")}</span>
                    )}
                </p>
                {secondaryAction}
                <Button type="submit" size="sm" loading={saving} disabled={invalid || saving}>
                    {t("reports.rates.save")}
                </Button>
            </div>
        </form>
    );
}
